import { useQuery } from '@apollo/client';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';

import goldenRatio from '@glass/common/modules/goldenRatio/goldenRatio';
import isValue from '@glass/common/modules/utils/isValue';
import { INDUSTRIES_SECTION_ID } from '@glass/web/modules/content/pages/contentIds';
import useLoadingResources from '@glass/web/modules/resources/useLoadingResources';

import BrowseLink from '@glass/shared/components/Link/BrowseLink';
import ContentSection from '@glass/shared/components/Section/ContentSection';
import IndustryGridItemLink from '@glass/shared/components/Section/IndustriesSection/IndustryGridItemLink';
import SectionButtonCta from '@glass/shared/components/Section/SectionButtonCta';
import SectionSpacer, { BOTTOM_EDGE } from '@glass/shared/components/Section/SectionSpacer';
import {
  ESTIMATED_NUM_INDUSTRIES,
  MAX_NUM_INDUSTRIES,
} from '@glass/shared/modules/content/maxResults';
import industriesQuery from '@glass/shared/modules/jobTitles/queries/industries.graphql';
import useIsBrowsePage from '@glass/shared/modules/resumes/hooks/useIsBrowsePage';
import withTrackingContext from '@glass/shared/modules/tracking/withTrackingContext';

const IndustriesSection = React.forwardRef(
  (
    {
      onItemClick,
      onSearchSuccess,
      title,
      subtitle,
      isAlternatingColor,
      type,
      outerSpacing,
      spacing,
      disableCta,
      skip,
      className,
    },
    ref,
  ) => {
    const { data, loading } = useQuery(industriesQuery, {
      variables: {
        first: MAX_NUM_INDUSTRIES,
        orderBy: 'name_ASC',
      },
      skip,
    });

    const loadingIndustries = useLoadingResources(ESTIMATED_NUM_INDUSTRIES);
    const isBrowsePage = useIsBrowsePage();

    const items = useMemo(
      () =>
        ((loading && !data?.industries?.length ? loadingIndustries : data?.industries) || []).map(
          ({ slug, name, width }) => (
            <IndustryGridItemLink
              key={slug}
              loading={loading}
              name={name}
              slug={slug}
              width={width}
              onClick={onItemClick}
            />
          ),
        ),
      [data, loading, loadingIndustries, onItemClick],
    );

    return (
      <ContentSection
        ref={ref}
        disableCta
        className={className}
        isAlternatingColor={isAlternatingColor}
        items={items}
        outerSpacing={outerSpacing}
        spacing={spacing}
        subtitle={subtitle}
        title={title}
        type={type}
        onSearchSuccess={onSearchSuccess}
      >
        {!disableCta && !isBrowsePage ? (
          <SectionSpacer
            edge={BOTTOM_EDGE}
            spacing={isValue(spacing) ? spacing / goldenRatio : undefined}
          >
            <SectionButtonCta component={BrowseLink} onClick={onItemClick}>
              Browse by any industry
            </SectionButtonCta>
          </SectionSpacer>
        ) : null}
      </ContentSection>
    );
  },
);

IndustriesSection.propTypes = {
  className: PropTypes.string,
  disableCta: PropTypes.bool,
  isAlternatingColor: PropTypes.bool,
  onItemClick: PropTypes.func,
  onSearchSuccess: PropTypes.func,
  outerSpacing: PropTypes.number,
  skip: PropTypes.bool,
  spacing: PropTypes.number,
  subtitle: PropTypes.string,
  title: PropTypes.string,
  type: PropTypes.string,
};

IndustriesSection.defaultProps = {
  title: `Search and browse $(resumeWords.plural)`,
  subtitle: `Use these <b>examples</b> to get started`,
};

export default React.memo(withTrackingContext(IndustriesSection, INDUSTRIES_SECTION_ID));
